import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../api/apiClient';

const StatusDot = ({ status }) => {
  const color =
    status === 'healthy' ? 'bg-green-500' :
    status === 'error'   ? 'bg-red-500' :
                           'bg-yellow-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${color} mr-2`} />;
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs text-ink-muted uppercase tracking-widest">{label}</p>
    <p className="text-base font-medium text-ink">{value ?? '—'}</p>
  </div>
);

export default function ServiceStatus({ serviceStatus, onRefresh }) {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiService.getMetrics?.()
      .then(data => setMetrics(data))
      .catch(err => console.error('ServiceStatus metrics error:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleRefresh = () => {
    onRefresh?.();
  };

  const statusLabel =
    serviceStatus?.status === 'healthy' ? t('content:statusOperational') :
    serviceStatus?.status === 'error'   ? t('content:statusError') :
                                          t('content:statusUnknown');

  /** Neo4j stats come from GET /metrics (database_stats), not always from /status */
  const databaseStats = serviceStatus?.database_stats ?? metrics?.database_stats;

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-ink-secondary uppercase tracking-widest">
          {t('content:statusServiceTitle')}
        </h2>
        <button
          onClick={handleRefresh}
          disabled={loading}
          aria-label={t('ui:refresh', 'Refresh')}
          className="text-xs text-ink-muted hover:text-accent-text transition-colors disabled:opacity-40"
        >
          {loading ? '···' : t('ui:refresh', 'Refresh')}
        </button>
      </div>

      {/* Service health */}
      <section aria-labelledby="status-service-heading">
        <h3 id="status-service-heading" className="text-xs text-ink-muted uppercase tracking-widest mb-3">
          {t('content:statusServiceTitle')}
        </h3>
        <div className="border border-border-subtle rounded p-4 space-y-3">
          <div className="flex items-center">
            <StatusDot status={serviceStatus?.status} />
            <span className="text-sm text-ink-secondary">{statusLabel}</span>
          </div>
          {serviceStatus?.system_health && (
            <div className="grid grid-cols-3 gap-4 pt-2">
              <Stat
                label={t('content:statusPipelineReady')}
                value={serviceStatus.system_health.pipeline_ready ? '✓' : '✗'}
              />
              <Stat label={t('content:statusNeo4jConnected')} value="✓" />
              <Stat
                label={t('content:statusMemory')}
                value={serviceStatus.system_health.memory_usage || '—'}
              />
            </div>
          )}
          {(serviceStatus?.error || serviceStatus?.system_health?.error) && (
            <p className="text-sm text-red-400 pl-3 border-l-2 border-red-400">
              {serviceStatus.error || serviceStatus.system_health?.error}
            </p>
          )}
        </div>
      </section>

      {/* Database stats */}
      <section aria-labelledby="status-db-heading">
        <h3 id="status-db-heading" className="text-xs text-ink-muted uppercase tracking-widest mb-3">
          {t('content:statusDbTitle')}
        </h3>
        <div className="border border-border-subtle rounded p-4">
          {databaseStats && (databaseStats.total_nodes != null || databaseStats.total_relationships != null) ? (
            <div className="grid grid-cols-2 gap-4">
              <Stat label={t('content:statusTotalNodes')} value={databaseStats.total_nodes} />
              <Stat label={t('content:statusRelationships')} value={databaseStats.total_relationships} />
              <div className="col-span-2">
                <p className="text-xs text-ink-muted">
                  {t('content:statusLastUpdated')}: {databaseStats.last_updated || '—'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-ink-muted">
              {loading ? '···' : t('content:statusNoStats')}
            </p>
          )}
        </div>
      </section>

      {/* Processing metrics */}
      {metrics && (
        <section aria-labelledby="status-metrics-heading">
          <h3 id="status-metrics-heading" className="text-xs text-ink-muted uppercase tracking-widest mb-3">
            {t('content:statusMetricsTitle')}
          </h3>
          <div className="border border-border-subtle rounded p-4 grid grid-cols-2 gap-4">
            <Stat label={t('content:statusProcessed')} value={metrics.total_processed} />
            <Stat label={t('content:statusSuccessRate')} value={`${Math.round(metrics.success_rate ?? 0)}%`} />
            <Stat
              label={t('content:statusProcessingTime')}
              value={typeof metrics.processing_time === 'number' ? `${metrics.processing_time.toFixed(1)}s` : '—'}
            />
            <Stat label={t('content:statusRecentActivity')} value={metrics.recent_activity?.length ?? 0} />
          </div>
        </section>
      )}

      {/* Active ETL jobs */}
      {serviceStatus?.active_jobs?.length > 0 && (
        <section aria-labelledby="status-active-heading">
          <h3 id="status-active-heading" className="text-xs text-ink-muted uppercase tracking-widest mb-3">
            {t('content:statusActiveJobs')}
          </h3>
          <ul className="border border-border-subtle rounded divide-y divide-border-subtle">
            {serviceStatus.active_jobs.map((job, i) => (
              <li key={i} className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-ink-secondary">{t('content:statusJobId')} {job.job_id}</span>
                <span className="text-xs text-ink-muted">{Math.round(job.progress || 0)}% {t('content:statusProgress')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
